import {
  Button,
  Flex,
  HelpIcon,
  Skeleton,
  Text,
  useModal,
  useTooltip,
  Box,
  useMatchBreakpoints,
  Balance,
  Pool,
  FlexGap,
} from '@pancakeswap/uikit'
import BigNumber from 'bignumber.js'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { PoolCategory } from 'config/constants/types'
import { useTranslation } from '@pancakeswap/localization'

import { useVaultPoolByKey } from 'state/pools/hooks'
import { VaultKey, DeserializedLockedCakeVault, IsLocked, Flexible2Locked, DeserializedLockedVaultUser } from 'state/types'
import { getVaultPosition, VaultPosition } from 'utils/cakePool'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { getBalanceNumber, getFullDisplayBalance } from '@pancakeswap/utils/formatBalance'
import { useProfileRequirement } from 'views/Pools/hooks/useProfileRequirement'
import isUndefinedOrNull from '@pancakeswap/utils/isUndefinedOrNull'
import useUserDataInVaultPresenter from 'views/Pools/components/LockedPool/hooks/useUserDataInVaultPresenter'
import { Token } from '@pancakeswap/sdk'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { findToken } from '@pancakeswap/tokens'

import { useCheckVaultApprovalStatus, useVaultApprove } from '../../../hooks/useApprove'
import VaultStakeModal from '../../CakeVaultCard/VaultStakeModal'
import NotEnoughTokensModal from '../../Modals/NotEnoughTokensModal'
import StakeModal from '../../Modals/StakeModal'
import { ProfileRequirementWarning } from '../../ProfileRequirementWarning'
import { ActionContainer, ActionContent, ActionTitles } from './styles'
import { VaultStakeButtonGroup } from '../../Vault/VaultStakeButtonGroup'
import AddCakeButton from '../../LockedPool/Buttons/AddCakeButton'
import ExtendButton from '../../LockedPool/Buttons/ExtendDurationButton'
import AfterLockedActions from '../../LockedPool/Common/AfterLockedActions'
import ConvertToLock from '../../LockedPool/Common/ConvertToLock'
import BurningCountDown from '../../LockedPool/Common/BurningCountDown'
import LockedStakedModal from '../../LockedPool/Modals/LockedStakeModal'

interface StackedActionProps {
  pool: Pool.DeserializedPool<Token>
}

const Staked: React.FunctionComponent<React.PropsWithChildren<StackedActionProps>> = ({ pool }) => {
  const {
    stakingLimit,
    isFinished,
    poolCategory,
    userData,
    stakingTokenPrice,
    vaultKey,
    profileRequirement,
    userDataLoaded,
  } = pool
  const { t } = useTranslation()
  const { account, chainId } = useActiveWeb3React()
  const { isMobile } = useMatchBreakpoints()

  const { isVaultApproved, setLastUpdated } = useCheckVaultApprovalStatus(vaultKey, chainId)
  const { handleApprove, pendingTx } = useVaultApprove(vaultKey, setLastUpdated)

  const isBnbPool = poolCategory === PoolCategory.BINANCE
  const stakedBalance = userData?.stakedBalance ? new BigNumber(userData.stakedBalance) : BIG_ZERO
  const isNotVaultAndHasStake = !vaultKey && stakedBalance.gt(0)

  const stakingTokenBalance = userData?.stakingTokenBalance ? new BigNumber(userData.stakingTokenBalance) : BIG_ZERO

  const stakingToken = findToken(pool.stakingToken.symbol, chainId)
  const stakedTokenBalance = getBalanceNumber(stakedBalance, stakingToken.decimals)
  // const stakedTokenDollarBalance = getBalanceNumber(
  //   stakingTokenBalance.multipliedBy(stakingTokenPrice),
  //   stakingToken.decimals,
  // )
  const stakedTokenDollarBalance = stakingTokenPrice
      ? getBalanceNumber(stakedBalance?.multipliedBy(stakingTokenPrice), stakingToken?.decimals)
      : 0;
  const vaultData = useVaultPoolByKey(pool.vaultKey)
  const {
    userData: {
      userShares,
      lastUserActionAmount,
      balance: { cakeAsBigNumber, cakeAsNumberBalance },
    },
  } = vaultData

  const isLocked = IsLocked(vaultKey) && (vaultData.userData as DeserializedLockedVaultUser).locked
  const isCakeVault = Flexible2Locked(vaultKey)===VaultKey.CakeVault
  const stakedVaultAmount = isCakeVault ? cakeAsBigNumber : isLocked ? (vaultData as DeserializedLockedCakeVault).userData?.lockedAmount : lastUserActionAmount
  const stakedVaultBalance = getBalanceNumber(stakedVaultAmount, stakingToken.decimals)
  const stakedVaultDollarBalance = getBalanceNumber(
    stakedVaultAmount.multipliedBy(stakingTokenPrice),
    stakingToken.decimals,
  )

  const { lockEndDate, remainingTime, burnStartTime } = useUserDataInVaultPresenter({
    lockStartTime:
      isLocked ? (vaultData as DeserializedLockedCakeVault).userData?.lockStartTime ?? '0' : '0',
    lockEndTime:
      isLocked ? (vaultData as DeserializedLockedCakeVault).userData?.lockEndTime ?? '0' : '0',
    burnStartTime:
      isLocked ? (vaultData as DeserializedLockedCakeVault).userData?.burnStartTime ?? '0' : '0',
  })

  const hasSharesStaked = userShares.gt(0)
  const isVaultWithShares = vaultKey && hasSharesStaked
  const needsApproval = !isVaultApproved

  const [onPresentTokenRequired] = useModal(<NotEnoughTokensModal tokenSymbol={stakingToken.symbol} />)

  const [onPresentStake] = useModal(
    <StakeModal
      isBnbPool={isBnbPool}
      pool={pool}
      stakingTokenBalance={stakingTokenBalance}
      stakingTokenPrice={stakingTokenPrice}
    />,
  )

  const [onPresentVaultStake] = useModal(<VaultStakeModal stakingMax={stakingTokenBalance} pool={pool} />)

  const [onPresentUnstake] = useModal(
    <StakeModal
      stakingTokenBalance={stakingTokenBalance}
      isBnbPool={isBnbPool}
      pool={pool}
      stakingTokenPrice={stakingTokenPrice}
      isRemovingStake
    />,
  )

  const [onPresentVaultUnstake] = useModal(<VaultStakeModal stakingMax={stakedVaultAmount} pool={pool} isRemovingStake />)

  const [openPresentLockedStakeModal] = useModal(
    <LockedStakedModal
      pool={pool}
      currentBalance={stakingTokenBalance}
      stakingToken={stakingToken}
      stakingTokenBalance={stakingTokenBalance}
    />,
  )

  const { notMeetRequired, notMeetThreshold } = useProfileRequirement(profileRequirement)

  const onStake = () => {
    if (vaultKey) {
      onPresentVaultStake()
    } else {
      onPresentStake()
    }
  }

  const onUnstake = () => {
    if (vaultKey) {
      onPresentVaultUnstake()
    } else {
      onPresentUnstake()
    }
  }

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t("You've already staked the maximum amount you can stake in this pool!"),
    { placement: 'bottom' },
  )

  const tooltipContentOfBurn = t(
    'After Burning starts at %burnStartTime%. You need to renew your fix-term position, to initiate a new lock or convert your staking position to flexible before it starts. Otherwise all the rewards will be burned within the next 90 days.',
    { burnStartTime },
  )
  const {
    targetRef: tagTargetRefOfBurn,
    tooltip: tagTooltipOfBurn,
    tooltipVisible: tagTooltipVisibleOfBurn,
  } = useTooltip(tooltipContentOfBurn, {
    placement: 'bottom',
  })

  const reachStakingLimit = stakingLimit.gt(0) && userData.stakedBalance.gte(stakingLimit)

  if (!account) {
    return (
      <ActionContainer>
        <ActionTitles>
          <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
            {t('Start staking')}
          </Text>
        </ActionTitles>
        <ActionContent>
          <ConnectWalletButton width="100%" />
        </ActionContent>
      </ActionContainer>
    )
  }

  if (!userDataLoaded) {
    return (
      <ActionContainer>
        <ActionTitles>
          <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
            {t('Start staking')}
          </Text>
        </ActionTitles>
        <ActionContent>
          <Skeleton width={180} height="32px" marginTop={14} />
        </ActionContent>
      </ActionContainer>
    )
  }

  if (notMeetRequired || notMeetThreshold) {
    return (
      <ActionContainer>
        <ActionTitles>
          <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
            {t('Enable pool')}
          </Text>
        </ActionTitles>
        <ActionContent>
          <ProfileRequirementWarning profileRequirement={profileRequirement} />
        </ActionContent>
      </ActionContainer>
    )
  }

  if (needsApproval && !isNotVaultAndHasStake && !isVaultWithShares) {
    return (
      <ActionContainer>
        <ActionTitles>
          <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
            {t('Enable pool')}
          </Text>
        </ActionTitles>
        <ActionContent>
          <Button width="100%" disabled={pendingTx} onClick={handleApprove}>
            {t('Enable %symbol%', {symbol: stakingToken.symbol})}
          </Button>
        </ActionContent>
      </ActionContainer>
    )
  }

  // Wallet connected, user data loaded and approved
  if (isNotVaultAndHasStake || isVaultWithShares) {
    const vaultPosition = getVaultPosition(vaultData.userData)
    return (
      <>
        <ActionContainer>
            <FlexGap flexDirection={isMobile?"column":"row"} justifyContent="space-between" height="100%" gap="10px">
              <Flex flex="1 1 0px" flexDirection={isMobile?"row":"column"} justifyContent={isMobile?"space-between":"flex-start"}>
                <Box>
                  <Text fontSize="12px" bold color="secondary" as="span" textTransform="uppercase">
                    {stakingToken.symbol}{' '}
                  </Text>
                  <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
                    {IsLocked(vaultKey) && (vaultData as DeserializedLockedCakeVault).userData.locked
                      ? t('Locked')
                      : t('Staked')}
                  </Text>
                </Box>
                <Box>
                  <Balance
                    mt="8px"
                    bold
                    lineHeight="1"
                    fontSize="20px"
                    decimals={5}
                    value={vaultKey ? stakedVaultBalance : stakedTokenBalance}
                  />
                  <Balance
                    fontSize="12px"
                    display="inline"
                    color="textSubtle"
                    decimals={2}
                    value={vaultKey ? stakedVaultDollarBalance : stakedTokenDollarBalance}
                    unit=" USD"
                    prefix="~"
                  />
                </Box>
              </Flex>
              {vaultPosition >= VaultPosition.Locked && (
                <Flex flex="1 1 0px" flexDirection={isMobile?"row":"column"} justifyContent={isMobile?"space-between":"flex-start"} alignItems={isMobile?"center":"flex-start"}>
                  <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase" >
                    {t('Unlocks In')}
                  </Text>
                  <Box>
                    <Flex alignItems="center" mt="8px">
                      <Text
                        bold
                        lineHeight="1"
                        fontSize="20px"
                        color={vaultPosition >= VaultPosition.LockedEnd ? '#D67E0A' : 'text'}
                      >
                        {vaultPosition >= VaultPosition.LockedEnd ? t('Unlocked') : remainingTime}
                        {tagTooltipVisibleOfBurn && tagTooltipOfBurn}
                      </Text>
                      <span ref={tagTargetRefOfBurn}>
                        <HelpIcon ml="4px" width="20px" height="20px" color="textSubtle" />
                      </span>
                    </Flex>
                    <Text
                      fontSize="12px"
                      display="inline"
                      color={vaultPosition >= VaultPosition.LockedEnd ? '#D67E0A' : 'text'}
                    >
                      {t('On %date%', { date: lockEndDate })}
                    </Text>
                  </Box>
                </Flex>
              )}
              {vaultPosition >= VaultPosition.LockedEnd && (
                <Flex flex="1 1 0px" flexDirection={isMobile?"row":"column"} justifyContent={isMobile?"space-between":"flex-start"} alignItems={isMobile?"center":"flex-start"}>
                  <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
                    {vaultPosition === VaultPosition.AfterBurning ? t('After Burning') : t('After Burning In')}
                  </Text>
                  <Text lineHeight="1" mt="8px" bold fontSize="20px" color="failure">
                    {vaultPosition === VaultPosition.AfterBurning ? (
                      isUndefinedOrNull((vaultData as DeserializedLockedCakeVault).userData.currentOverdueFee) ? (
                        '-'
                      ) : (
                        t('%amount% Burned', {
                          amount: getFullDisplayBalance(
                            (vaultData as DeserializedLockedCakeVault).userData.currentOverdueFee,
                            18,
                            5,
                          ),
                        })
                      )
                    ) : (
                      <BurningCountDown lockEndTime={(vaultData as DeserializedLockedCakeVault).userData.lockEndTime} />
                    )}
                  </Text>
                </Flex>
              )}
              {(vaultPosition === VaultPosition.Flexible || !vaultKey) && (
                <FlexGap width="100%" gap="0.5em" flex="0 0 8em" flexDirection="column" justifyContent="flex-end">
                  <Button variant="secondary" onClick={onUnstake}>
                    Pull out
                  </Button>
                  {reachStakingLimit ? (
                    <span ref={targetRef}>
                      <Button variant="secondary" disabled>
                        Insert
                      </Button>
                    </span>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={stakingTokenBalance.gt(0) ? onStake : onPresentTokenRequired}
                      disabled={isFinished}
                    >
                      Insert
                    </Button>
                  )}
                </FlexGap>
              )}            
            </FlexGap>
            {vaultPosition === VaultPosition.Locked && (
              <FlexGap flex="1 1 0px" width="100%" gap="12px" mt="8px">
                <div style={{flex:"1 1 50%"}}>
                  <AddCakeButton
                    pool={pool}
                    lockEndTime={(vaultData as DeserializedLockedCakeVault).userData.lockEndTime}
                    lockStartTime={(vaultData as DeserializedLockedCakeVault).userData.lockStartTime}
                    currentLockedAmount={vaultKey ? stakedVaultAmount : stakedBalance}
                    stakingToken={stakingToken}
                    currentBalance={stakingTokenBalance}
                    stakingTokenBalance={stakingTokenBalance}
                    disabled={isFinished}
                  />
                </div>
                <div style={{flex:"1 1 50%"}}>
                  <ExtendButton
                    pool={pool}
                    lockEndTime={(vaultData as DeserializedLockedCakeVault).userData.lockEndTime}
                    lockStartTime={(vaultData as DeserializedLockedCakeVault).userData.lockStartTime}
                    stakingToken={stakingToken}
                    currentBalance={stakingTokenBalance}
                    currentLockedAmount={vaultKey ? stakedVaultBalance : stakedTokenBalance}
                    disabled={isFinished}
                  >
                    {t('Extend')}
                  </ExtendButton>
                </div>
              </FlexGap>
            )}
            
            
            {tooltipVisible && tooltip}
        </ActionContainer>
        
        {[VaultPosition.AfterBurning, VaultPosition.LockedEnd].includes(vaultPosition) && (
          <Box
            width="100%"
            mt={['0', '0', '24px', '24px', '24px']}
            ml={['0', '0', '12px', '12px', '12px', '32px']}
            mr={['0', '0', '12px', '12px', '12px', '0px']}
          >
            <AfterLockedActions
              isInline
              pool={pool}
              position={vaultPosition}
              currentLockedAmount={cakeAsNumberBalance}
              stakingToken={stakingToken}
              lockEndTime="0"
              lockStartTime="0"
            />
          </Box>
        )}
        {IsLocked(vaultKey) && vaultPosition === VaultPosition.Flexible && (
          <Box
            width="100%"
            mt={['0', '0', '30px', '30px', '30px']}
            ml={['0', '0', '12px', '12px', '32px']}
            mr={['0', '0', '12px', '12px', '0']}
          >
            <ConvertToLock pool={pool} stakingToken={stakingToken} currentStakedAmount={stakedVaultBalance} isInline />
          </Box>
        )}
      </>
    )
  }

  return (
    <ActionContainer>
      <ActionTitles>
        <Text fontSize="12px" bold color="secondary" as="span" textTransform="uppercase">
          {t('Stake')}{' '}
        </Text>
        <Text fontSize="12px" bold color="textSubtle" as="span" textTransform="uppercase">
          {stakingToken.symbol}
        </Text>
      </ActionTitles>
      <ActionContent>
        {vaultKey ? (
          <VaultStakeButtonGroup
            symbol={stakingToken.symbol}
            onFlexibleClick={stakingTokenBalance.gt(0) ? onStake : onPresentTokenRequired}
            onLockedClick={IsLocked(vaultKey) ? openPresentLockedStakeModal : null}
            disabled={isFinished}
          />
        ) : (
          <Button
            width="100%"
            onClick={stakingTokenBalance.gt(0) ? onStake : onPresentTokenRequired}
            disabled={isFinished}
          >
            {t('Stake')}
          </Button>
        )}
      </ActionContent>
    </ActionContainer>
  )
}

export default Staked
